import { memo } from 'react';
import type { StoreApi } from 'zustand';
import { ArrowRightLeft, CircleCheck, CircleDashed, CircleMinus, Flag, Lock, MapPin, OctagonAlert } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { SerialDisposition } from '../../types';
import type { AdjustCardModel, AdjustRowModel, AdjustSumTip, BreakdownModel } from '../../utils/adjustModel';
import type { AdjustDialogState } from './adjustDialogStore';

const DISPOSITIONS: [SerialDisposition, string][] = [
  [SerialDisposition.ReturnedToBin, 'Returned to bin'],
  [SerialDisposition.Damaged, 'Damaged'],
  [SerialDisposition.Missing, 'Missing'],
];

const SUM_ICON = {
  'circle-dashed': CircleDashed,
  'circle-check': CircleCheck,
  'circle-minus': CircleMinus,
} as const;

const TONE = {
  warning: 'bg-warning/10 text-warning',
  success: 'bg-success/10 text-success',
} as const;

interface AdjustBinCardProps {
  card: AdjustCardModel;
  store: StoreApi<AdjustDialogState>;
  /** The row of THIS card whose breakdown card is open, else null (so other cards' props do not change). */
  openPlanKey: string | null;
}

/**
 * PT-6 scope 3 — one bin as a card: a muted header band (map-pin tile, bin code, meta, and the balance pill), then
 * its rows. Memoised on the card's `sig` (everything it renders), so typing in one box
 * re-renders that card, the cards holding rows of the same MRS line, and nothing else.
 */
export const AdjustBinCard = memo(
  function AdjustBinCard({ card, store, openPlanKey }: AdjustBinCardProps) {
    const SumIcon = card.sum ? SUM_ICON[card.sum.icon] : null;
    return (
      <div className="overflow-hidden rounded-[10px] border border-border bg-card" data-adjust-card={card.bin}>
        <div className="flex items-center gap-2.5 bg-muted/45 px-3.5 py-2.5">
          <span aria-hidden className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px] border border-border bg-card text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[13px] font-semibold">{card.bin}</div>
            <div className="text-[11.5px] tabular-nums text-muted-foreground">{card.meta}</div>
          </div>
          {card.sum && SumIcon && (
            <div className="flex shrink-0 items-center gap-2">
              <span className="whitespace-nowrap text-[11.5px] tabular-nums text-muted-foreground">{card.sum.meta}</span>
              {/* One per card, so a bounded Radix tooltip (UI-GUIDELINES §8/§9); focusable so a keyboard reaches it. */}
              <Tooltip delayDuration={120} disableHoverableContent>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    aria-label={sumTipText(card.sum.tip)}
                    className={cn('inline-flex h-[22px] cursor-help items-center gap-[5px] whitespace-nowrap rounded-full px-[9px] text-[11px] font-semibold tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring', TONE[card.sum.tone])}
                  >
                    <SumIcon className="h-3 w-3" aria-hidden />
                    {card.sum.pill}
                  </span>
                </TooltipTrigger>
                <SumTipCard tip={card.sum.tip} />
              </Tooltip>
            </div>
          )}
        </div>
        {card.rows.map((row) => (
          <AdjustRow key={row.key} row={row} store={store} planOpen={openPlanKey === row.key} />
        ))}
      </div>
    );
  },
  (a, b) => a.card.sig === b.card.sig && a.openPlanKey === b.openPlanKey && a.store === b.store,
);

function AdjustRow({ row, store, planOpen }: { row: AdjustRowModel; store: StoreApi<AdjustDialogState>; planOpen: boolean }) {
  const actions = store.getState();

  return (
    <div className="border-t border-border/60" data-row-key={row.key}>
      <div
        className={cn(
          'grid grid-cols-[minmax(0,1fr)_120px_204px] items-center gap-x-3 py-[9px] pl-[50px] pr-3.5',
          row.kind === 'locked' && 'opacity-55',
        )}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {row.serial && (
            <span className="inline-flex h-5 items-center rounded-[5px] border border-border bg-muted/50 px-[7px] font-mono text-[11px] font-semibold">
              {row.serial}
            </span>
          )}
          {row.mrsNumber && (
            <span className="inline-flex h-5 items-center whitespace-nowrap rounded-[5px] border border-border px-[7px] font-mono text-[11px] font-medium text-info">
              {row.mrsNumber}
            </span>
          )}
          {row.mrsSub && (
            <span className="min-w-0 basis-full truncate text-[11px] leading-[1.35] text-muted-foreground" title={row.mrsSub}>
              {row.mrsSub}
            </span>
          )}
          {row.plain && <span className="text-xs text-muted-foreground">Picked quantity</span>}
          {/* Owner, 2026-10-05 — this dialog is about what can still change, so a row carries no slip number, and a
              posted figure only where it is a limit: a PARTLY posted row says what can change and what is locked.
              A row with nothing left to change says "fully posted" on the right. Beside the chip when there is
              room, under it when there is not. Native `title`: it repeats per row (UI-GUIDELINES §8). */}
          {row.postedNote && (
            <span
              title={row.postedNote.tip}
              className="inline-flex cursor-help items-center gap-1 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground"
            >
              {row.postedNote.open}
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-[3px] text-warning">
                <Lock className="h-[10px] w-[10px]" aria-hidden />
                {row.postedNote.posted}
              </span>
            </span>
          )}
        </div>

        <div className="min-w-0 text-right text-[11px] text-muted-foreground">
          <div className="truncate" title={row.curLabel}>
            {row.curLabel}
          </div>
          {row.planLabel && row.breakdown && (
            <div className="mt-px truncate">
              <PlanLabel row={row} breakdown={row.breakdown} open={planOpen} store={store} />
            </div>
          )}
        </div>

        <div className="flex min-w-0 items-center gap-2">
          {row.kind === 'taken' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  aria-label={`Swap needed — ${row.takenLabel}`}
                  className="inline-flex h-[22px] cursor-help items-center gap-1 whitespace-nowrap rounded-full bg-destructive/10 px-[9px] text-[11px] font-semibold text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <OctagonAlert className="h-[11px] w-[11px]" aria-hidden />
                  Swap needed
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{row.takenLabel}</TooltipContent>
            </Tooltip>
          )}
          {row.kind === 'locked' && (
            <span className="w-[110px] text-right text-[11.5px] italic text-muted-foreground">{row.lockedLabel}</span>
          )}
          {row.kind === 'qty' && (
            <Input
              type="number"
              // Owner, 2026-10-05 — a row only goes down: the arrows stop at the posted floor and at what is there now.
              min={row.floor}
              max={row.current}
              value={row.value}
              onChange={(e) => actions.setVal(row.key, e.target.value, row.current)}
              title="Now"
              aria-label="Now"
              aria-invalid={row.error != null}
              className={cn(
                'h-8 w-[110px] rounded-[7px] px-2.5 text-right text-[13px] font-medium md:text-[13px]',
                row.error != null && 'border-destructive',
              )}
            />
          )}
          {row.canMoveQty && (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* aria-disabled, not disabled: a disabled button shows no tooltip, and the tooltip is the reason. */}
                <button
                  type="button"
                  onClick={() => openMove(row, store)}
                  aria-label={row.moveTip}
                  aria-disabled={row.moveDisabled}
                  aria-haspopup="dialog"
                  className={cn(
                    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[7px] border border-transparent text-muted-foreground transition-colors focus-visible:border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    row.moveDisabled
                      ? 'cursor-not-allowed opacity-40'
                      : 'hover:border-input hover:bg-muted/60 hover:text-foreground',
                  )}
                >
                  <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent>{row.moveTip}</TooltipContent>
            </Tooltip>
          )}
          {row.kind === 'serial' && <SerialSegment row={row} store={store} />}
        </div>
      </div>

      {row.disposition && (
        <div className="bg-muted/25 pb-3 pl-[50px] pr-3.5 pt-0.5">
          <div className="mt-[9px] flex flex-wrap items-center gap-2">
            <span className="text-[11.5px] font-semibold">
              {row.disposition.question} <span className="text-destructive">*</span>
            </span>
            <div className="flex flex-wrap gap-[5px]" role="group" aria-label={row.disposition.question}>
              {DISPOSITIONS.map(([value, label]) => {
                const chosen = row.disposition!.value === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={chosen}
                    onClick={() => (row.disposition!.target === 'serial'
                      ? actions.setSerDisp(row.key, value)
                      : actions.setDisp(row.displayBinId, value))}
                    className={cn(
                      'h-[25px] rounded-full border px-2.5 text-[11.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      chosen ? 'border-primary bg-primary/[0.08] text-primary' : 'border-border bg-card text-muted-foreground',
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          {(row.disposition.value === SerialDisposition.Damaged || row.disposition.value === SerialDisposition.Missing) && (
            <div className="mt-2 flex items-start gap-1.5 text-[11px] leading-[1.45] text-warning">
              <Flag className="mt-px h-3 w-3 shrink-0" aria-hidden />
              <span>{row.disposition.flagText}</span>
            </div>
          )}
          {row.disposition.incomplete && (
            <div className="mt-[7px] text-[11px] text-muted-foreground">Choose where the units go to include this change.</div>
          )}
        </div>
      )}

      {row.error && <div className="pb-[9px] pl-[50px] pr-3.5 text-[11px] text-destructive">{row.error}</div>}
    </div>
  );
}

/** PT-6b — open the Move dialog for this row, unless its move control is disabled (its tooltip says why). */
function openMove(row: AdjustRowModel, store: StoreApi<AdjustDialogState>) {
  if (row.moveDisabled || row.lineId == null || row.slotId == null) return;
  store.getState().openMove({ lineId: row.lineId, slotId: row.slotId });
}

/** The words of the pill's card, for a screen reader. */
function sumTipText(tip: AdjustSumTip): string {
  return [
    `${tip.bin}: ${tip.state}.`,
    ...tip.figures.map((f) => `${f.label}: ${f.value}.`),
    `Adjust can: ${tip.can.join('; ')}.`,
    `It can't: ${tip.cannot}`,
    tip.next,
  ].filter(Boolean).join(' ');
}

/**
 * The balance pill's hover card (owner, 2026-09-30): the bin's figures, then what Adjust can and cannot do — it
 * lowers or moves a recorded pick, and never adds units. Styled like the planned-label breakdown card.
 */
function SumTipCard({ tip }: { tip: AdjustSumTip }) {
  return (
    <TooltipContent
      side="bottom"
      align="end"
      sideOffset={6}
      collisionPadding={12}
      className="pointer-events-none w-[280px] rounded-[10px] border border-border bg-card px-3 py-2.5 text-foreground shadow-lg"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate font-mono text-xs font-semibold">{tip.bin}</span>
        <span className="whitespace-nowrap text-[11.5px] text-muted-foreground">{tip.state}</span>
      </div>
      <div className="mt-2 flex flex-col gap-[3px] text-xs tabular-nums">
        {tip.figures.map((f) => (
          <div key={f.label} className="flex justify-between gap-2">
            <span className="text-muted-foreground">{f.label}</span>
            <b className={cn('font-semibold', f.tone === 'warning' && 'text-warning')}>
              {f.value}
            </b>
          </div>
        ))}
      </div>
      <div className="mt-2 border-t border-border pt-2 text-[11.5px] leading-[1.45]">
        <div className="font-semibold">Adjust can</div>
        <ul className="mt-0.5 flex flex-col gap-0.5">
          {tip.can.map((c) => (
            <li key={c} className="flex gap-1.5">
              <CircleCheck className="mt-[3px] h-3 w-3 shrink-0 text-success" aria-hidden />
              <span>{c}</span>
            </li>
          ))}
        </ul>
        <div className="mt-1.5 font-semibold">Adjust can’t</div>
        <div className="mt-0.5 flex gap-1.5">
          <CircleMinus className="mt-[3px] h-3 w-3 shrink-0 text-destructive" aria-hidden />
          <span>{tip.cannot}</span>
        </div>
      </div>
      {tip.next && (
        <div className="mt-2 rounded-md bg-muted/70 px-2 py-1.5 text-[11.5px] leading-[1.45]">{tip.next}</div>
      )}
    </TooltipContent>
  );
}

/** The dotted planned label; its breakdown card opens on hover or focus (a card-styled tooltip, never plain). */
function PlanLabel({ row, breakdown, open, store }: {
  row: AdjustRowModel; breakdown: BreakdownModel; open: boolean; store: StoreApi<AdjustDialogState>;
}) {
  return (
    <Tooltip open={open} onOpenChange={(o) => store.getState().setPlanKey(o ? row.key : null)} delayDuration={120} disableHoverableContent>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={row.planTip}
          className="cursor-help text-muted-foreground/85 underline decoration-dotted underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {row.planLabel}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="end"
        sideOffset={6}
        collisionPadding={12}
        className="pointer-events-none w-[264px] rounded-[10px] border border-border bg-card px-3 py-2.5 text-foreground shadow-lg"
      >
        <div className="truncate font-mono text-xs font-semibold">{breakdown.title}</div>
        <div className="mt-px text-[11.5px] text-muted-foreground">{breakdown.sub}</div>
        <div className="mt-2 flex flex-col gap-0.5">
          {breakdown.rows.map((q, i) => (
            <div key={`${q.bin}-${i}`} className={cn('grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-md px-1.5 py-1', q.isThis && 'bg-muted/70')}>
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="font-mono text-[11.5px] font-semibold">{q.bin}</span>
                {q.isThis && (
                  <span className="inline-flex h-4 items-center rounded-full bg-background px-1.5 text-[10px] font-semibold text-muted-foreground ring-1 ring-inset ring-border">
                    this row
                  </span>
                )}
              </span>
              <span className="whitespace-nowrap text-xs tabular-nums">
                {q.was && <span className="mr-1.5 text-[11px] text-muted-foreground line-through">{q.was}</span>}
                <b className="font-semibold">{q.value}</b>
              </span>
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between gap-2 border-t border-border pt-2 text-xs tabular-nums">
          <span className="text-muted-foreground">On this MRS line</span>
          <b className={cn('font-semibold', breakdown.over && 'text-destructive')}>{breakdown.sumLabel}</b>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function SerialSegment({ row, store }: { row: AdjustRowModel; store: StoreApi<AdjustDialogState> }) {
  const actions = store.getState();
  const seg = (on: boolean, tone: string) =>
    cn(
      'h-[26px] rounded-md px-2.5 text-[11.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      on ? cn('bg-card shadow-sm', tone) : 'text-muted-foreground',
    );
  return (
    <div className="inline-flex gap-0.5 rounded-lg bg-muted p-0.5" role="group" aria-label={`${row.serial ?? 'Serial'}: keep, unpick or move`}>
      <button type="button" aria-pressed={row.serMode === 'keep'} onClick={() => actions.setSerMode(row.key, 'keep')} className={seg(row.serMode === 'keep', 'text-foreground')}>
        Keep
      </button>
      <button
        type="button"
        aria-pressed={row.serMode === 'return'}
        title="Take this serial off the pick"
        onClick={() => actions.setSerMode(row.key, 'return')}
        className={seg(row.serMode === 'return', 'text-warning')}
      >
        Unpick
      </button>
      {row.canMoveSer && (
        // PT-6b — Move… is not a third state of the segment: it opens the Move dialog, which saves on its own.
        <button
          type="button"
          aria-disabled={row.moveDisabled}
          aria-haspopup="dialog"
          title={row.moveTip}
          onClick={() => openMove(row, store)}
          className={cn(seg(false, 'text-info'), row.moveDisabled && 'cursor-not-allowed opacity-40')}
        >
          Move…
        </button>
      )}
    </div>
  );
}
